import type { UserEntity, OrgMembershipEntity, ProjectMembershipEntity, OrgSummaryEntity, PermissionRuleEntity } from '../../domain/entities/UserEntity.js';
import type {
  IUserRepository,
  UserCreateInput,
  UserUpdateInput,
} from '../../domain/repositories/IUserRepository.js';
import {
  UserRemoteDataSource,
  type UserRemoteListOptions,
  type UserRemoteUpsertPayload,
  type UserRemoteUpdatePayload,
} from '../datasources/UserRemoteDataSource.js';
import type { UserDto } from '../dtos/UserDto.js';

function mapDtoToEntity(dto: UserDto): UserEntity {
  const name = (dto.displayName ?? '').trim();
  const [fallbackFirstName = '', ...restNameParts] = name.split(/\s+/).filter(Boolean);
  const fallbackLastName = restNameParts.join(' ');

  return {
    id: dto.id ?? dto.uid,
    firstName: dto.firstName ?? fallbackFirstName,
    lastName: dto.lastName ?? fallbackLastName,
    email: dto.email ?? '',
    phone: dto.phone ?? null,
    createdAt: dto.createdAt ?? null,
    updatedAt: dto.updatedAt ?? null,
    isSuperAdmin: dto.isSuperAdmin ?? false,
    provinceCode: dto.provinceCode ?? null,
    birthDate: dto.birthDate ?? null,
    gender: dto.gender ?? null,
    permanentAddress: dto.permanentAddress ?? null,
    contactAddress: dto.contactAddress ?? null,
    idCardNumber: dto.idCardNumber ?? null,
    taxCode: dto.taxCode ?? null,
    bankAccountNumber: dto.bankAccountNumber ?? null,
    bankName: dto.bankName ?? null,
    bankBranch: dto.bankBranch ?? null,
    heightCm: dto.heightCm ?? null,
    weightKg: dto.weightKg ?? null,
    workExperiences: dto.workExperiences ?? null,
    avatarUrl: dto.avatarUrl ?? null,
    idCardFrontUrl: dto.idCardFrontUrl ?? null,
    idCardBackUrl: dto.idCardBackUrl ?? null,
    photos: dto.photos ?? null,
  };
}

export class UserRepository implements IUserRepository {
  constructor(private readonly remoteDataSource: UserRemoteDataSource) {}

  async getById(uid: string): Promise<UserEntity | null> {
    const dto = await this.remoteDataSource.getUserById(uid);
    return dto ? mapDtoToEntity(dto) : null;
  }

  async getByIds(ids: string[]): Promise<UserEntity[]> {
    const dtos = await this.remoteDataSource.getUsersByIds(ids);
    return dtos.map(mapDtoToEntity);
  }

  async getByEmails(emails: string[]): Promise<UserEntity[]> {
    const dtos = await this.remoteDataSource.getUsersByEmails(emails);
    return dtos.map(mapDtoToEntity);
  }

  async list(options?: UserRemoteListOptions): Promise<{ data: UserEntity[]; total: number }> {
    const { data, total } = await this.remoteDataSource.listUsers(options);
    return { data: data.map(mapDtoToEntity), total };
  }

  async create(input: UserCreateInput): Promise<UserEntity> {
    const source = input as any;
    const payload: UserRemoteUpsertPayload = {
      uid: String(source.uid ?? source.id ?? ''),
      email: String(source.email ?? ''),
      firstName: (source.firstName as string | undefined) ?? null,
      lastName: (source.lastName as string | undefined) ?? null,
      phone: (source.phone as string | undefined) ?? null,
      displayName: (source.displayName as string | undefined) ?? null,
    };
    await this.remoteDataSource.upsertUser(payload);
    const entity = await this.getById(String(source.uid ?? source.id ?? ''));
    if (!entity) throw new Error('User creation failed');
    return entity;
  }

  async update(uid: string, input: UserUpdateInput): Promise<UserEntity | null> {
    const payload: UserRemoteUpdatePayload = {};
    if (input.email !== undefined) payload.email = input.email;
    if (input.firstName !== undefined) payload.firstName = input.firstName;
    if (input.lastName !== undefined) payload.lastName = input.lastName;
    if (input.phone !== undefined) payload.phone = input.phone;
    if (input.provinceCode !== undefined) payload.provinceCode = input.provinceCode;
    if (input.birthDate !== undefined) payload.birthDate = input.birthDate;
    if (input.gender !== undefined) payload.gender = input.gender;
    if (input.permanentAddress !== undefined) payload.permanentAddress = input.permanentAddress;
    if (input.contactAddress !== undefined) payload.contactAddress = input.contactAddress;
    if (input.idCardNumber !== undefined) payload.idCardNumber = input.idCardNumber;
    if (input.taxCode !== undefined) payload.taxCode = input.taxCode;
    if (input.bankAccountNumber !== undefined) payload.bankAccountNumber = input.bankAccountNumber;
    if (input.bankName !== undefined) payload.bankName = input.bankName;
    if (input.bankBranch !== undefined) payload.bankBranch = input.bankBranch;
    if (input.heightCm !== undefined) payload.heightCm = input.heightCm;
    if (input.weightKg !== undefined) payload.weightKg = input.weightKg;
    if (input.workExperiences !== undefined) payload.workExperiences = input.workExperiences;
    if (input.avatarUrl !== undefined) payload.avatarUrl = input.avatarUrl;
    if (input.idCardFrontUrl !== undefined) payload.idCardFrontUrl = input.idCardFrontUrl;
    if (input.idCardBackUrl !== undefined) payload.idCardBackUrl = input.idCardBackUrl;
    if (input.photos !== undefined) payload.photos = input.photos;
    const ok = await this.remoteDataSource.updateUser(uid, payload);
    if (!ok) throw new Error('Bạn không có quyền chỉnh sửa người dùng này');
    return this.getById(uid);
  }

  async upsert(input: UserCreateInput & UserUpdateInput, ignoreDuplicates?: boolean): Promise<UserEntity> {
    const source = input as any;
    const payload: UserRemoteUpsertPayload = {
      uid: String(source.uid ?? source.id ?? ''),
      email: String(source.email ?? ''),
      firstName: (source.firstName as string | undefined) ?? null,
      lastName: (source.lastName as string | undefined) ?? null,
      phone: (source.phone as string | undefined) ?? null,
      displayName: (source.displayName as string | undefined) ?? null,
    };
    await this.remoteDataSource.upsertUser(payload, ignoreDuplicates);
    const entity = await this.getById(String(source.uid ?? source.id ?? ''));
    if (!entity) throw new Error('User upsert failed');
    return entity;
  }

  async getOrgMembership(profileId: string, orgId: string): Promise<OrgMembershipEntity | null> {
    const row = await this.remoteDataSource.getOrgMembership(profileId, orgId);
    if (!row) return null;
    return {
      roleId: row.role?.id ?? row.role_id ?? '',
      roleName: row.role?.name ?? '',
      joinedAt: row.created_at ?? '',
    };
  }

  async getProjectMemberships(profileId: string, orgId: string): Promise<ProjectMembershipEntity[]> {
    const rows = await this.remoteDataSource.getProjectMemberships(profileId, orgId);
    return rows.map((row) => ({
      projectId: row.project?.id ?? row.project_id ?? '',
      projectName: row.project?.name ?? row.project_id ?? '',
      roleId: row.role?.id ?? row.role_id ?? '',
      roleName: row.role?.name ?? '',
      joinedAt: row.created_at ?? '',
    }));
  }

  async getMyOrgs(profileId: string): Promise<OrgSummaryEntity[]> {
    const rows = await this.remoteDataSource.getMyOrgs(profileId);
    return rows.map((row) => ({
      id: row.id ?? '',
      name: row.name ?? '',
      description: row.description ?? null,
      joinedAt: row.created_at ?? '',
    }));
  }

  async getMyPermissions(orgId: string): Promise<PermissionRuleEntity[]> {
    const rows = await this.remoteDataSource.getMyPermissions(orgId);
    return rows.map((row) => ({
      resource: row.resource ?? '',
      action: row.action ?? '',
      effect: (row.effect === 'deny' ? 'deny' : 'allow') as 'allow' | 'deny',
    }));
  }

  async getMyProjectPermissions(orgId: string, projectId: string): Promise<PermissionRuleEntity[]> {
    const rows = await this.remoteDataSource.getMyProjectPermissions(orgId, projectId);
    return rows.map((row) => ({
      resource: row.resource ?? '',
      action: row.action ?? '',
      effect: (row.effect === 'deny' ? 'deny' : 'allow') as 'allow' | 'deny',
    }));
  }
}
